import React from 'react';
import { Badge, BadgeProps, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { StatusTone } from '../guideTokens';
import './GuideComponents.css';

const pill = (tone: StatusTone) => ({
  color: `var(--ichtys-pill-${tone}-fg)`,
  backgroundColor: `var(--ichtys-pill-${tone}-bg)`,
  ...shorthands.borderColor(`var(--ichtys-pill-${tone}-border)`),
});

const useStatusPillStyles = makeStyles({
  // Guía, "Etiquetas de estado": pastilla con borde, fondo tenue del mismo tono, MAYÚSCULAS bold.
  base: {
    textTransform: 'uppercase',
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '0.03em',
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.borderWidth('var(--ichtys-pill-border-width, 1px)'),
    ...shorthands.borderStyle('solid'),
  },
  success: pill('success'),
  warning: pill('warning'),
  info: pill('info'),
  brand: pill('brand'),
  confirmed: pill('confirmed'),
  danger: pill('danger'),
  neutral: pill('neutral'),
});

/** Color de Badge de Fluent → tono de la guía. */
export const toneFromBadgeColor = (color: BadgeProps['color']): StatusTone => {
  switch (color) {
    case 'success':
      return 'success';
    case 'warning':
    case 'severe':
      return 'warning';
    case 'danger':
    case 'important':
      return 'danger';
    case 'brand':
      return 'info';
    default:
      return 'neutral';
  }
};

/**
 * Símbolo por tono para alto contraste (el estado nunca depende sólo del color): `● ACTIVO`,
 * `! SIN REVISAR`, `✓ REVISADO`, `■ FINALIZADO`, `◆ CONFIRMADO`, `○ INACTIVO`; error `×`.
 * `✓ PAGADO` (éxito) se pide con `symbol="✓"`.
 */
export const STATUS_PILL_SYMBOLS: Record<StatusTone, string> = {
  success: '●',
  warning: '!',
  info: '✓',
  brand: '■',
  confirmed: '◆',
  danger: '×',
  neutral: '○',
};

export type StatusPillProps = Omit<BadgeProps, 'appearance' | 'shape'> & {
  /** Tono de la guía; si no se indica, se deduce de `color`. */
  tone?: StatusTone;
  /** Símbolo que se antepone en alto contraste; por defecto el del tono (`STATUS_PILL_SYMBOLS`). */
  symbol?: string;
};

/**
 * Etiqueta de estado de la guía sobre el Badge de Fluent. El estado nunca depende sólo del color:
 * siempre lleva texto y, en alto contraste, el símbolo del tono antes del texto (regla CSS sobre
 * `data-symbol` en GuideComponents.css). Colores en variables `--ichtys-pill-*` por modo.
 */
export const StatusPill = ({ tone, symbol, className, color, ...rest }: StatusPillProps) => {
  const styles = useStatusPillStyles();
  const resolved = tone ?? toneFromBadgeColor(color);
  return (
    <Badge
      {...rest}
      color={color}
      appearance="outline"
      shape="circular"
      data-tone={resolved}
      data-symbol={symbol ?? STATUS_PILL_SYMBOLS[resolved]}
      className={mergeClasses('ichtys-status-pill', styles.base, styles[resolved], className)}
    />
  );
};

export default StatusPill;
